import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Loader2, XCircle, Home } from 'lucide-react';
import { getCheckoutStatus } from '@/lib/api';
import { useCartStore } from '@/lib/store';

export default function ShopSuccess({ type = 'shop' }) {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const clearCart = useCartStore((state) => state.clearCart);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    let attemptCount = 0;

    const pollStatus = async () => {
      if (cancelled) return;
      if (attemptCount >= 5) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await getCheckoutStatus(sessionId);
        if (cancelled) return;

        if (response.payment_status === 'paid') {
          setStatus('success');
          clearCart();
        } else if (response.status === 'expired') {
          setStatus('expired');
        } else {
          attemptCount++;
          setTimeout(pollStatus, 2000);
        }
      } catch {
        if (!cancelled) setStatus('error');
      }
    };

    pollStatus();
    return () => { cancelled = true; };
  }, [sessionId, clearCart]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <Loader2 className="w-16 h-16 text-champagne animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-night mb-4">
              Verifica Pagamento...
            </h1>
            <p className="font-body text-stone-500">
              Stiamo verificando il tuo pagamento. Non chiudere questa pagina.
            </p>
          </>
        );
      
      case 'success':
        return (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Check className="text-white" size={40} />
            </motion.div>
            <h1 className="font-heading text-3xl text-night mb-4">
              Pagamento Completato!
            </h1>
            <p className="font-body text-stone-500 mb-8">
              {type === 'shop'
                ? 'Grazie per il tuo acquisto! Riceverai una conferma via email con i dettagli della spedizione.'
                : 'Grazie per il tuo acquisto! Il tuo abbonamento è ora attivo.'}
            </p>
            <Button
              asChild
              className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8 py-6 text-sm uppercase tracking-widest"
            >
              <Link to="/">
                <Home className="mr-2" size={16} />
                Torna alla Home
              </Link>
            </Button>
          </>
        );
      
      case 'expired':
      case 'error':
      case 'timeout':
        return (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <XCircle className="text-white" size={40} />
            </motion.div>
            <h1 className="font-heading text-3xl text-night mb-4">
              {status === 'expired' ? 'Sessione Scaduta' : 'Errore'}
            </h1>
            <p className="font-body text-stone-500 mb-8">
              {status === 'expired'
                ? 'La sessione di pagamento è scaduta. Riprova.'
                : 'Si è verificato un errore. Per favore contattaci se hai completato il pagamento.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                variant="outline"
                className="border-night text-night hover:bg-night hover:text-ivory rounded-full px-6"
              >
                <Link to={type === 'shop' ? '/shop' : '/abbonamenti'}>
                  Riprova
                </Link>
              </Button>
              <Button
                asChild
                className="bg-night text-ivory hover:bg-stone-800 rounded-full px-6"
              >
                <Link to="/contatti">
                  Contattaci
                </Link>
              </Button>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="bg-ivory pt-32 min-h-screen" data-testid="success-page">
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        {renderContent()}
      </div>
    </div>
  );
}
